import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { styled, alpha } from '@mui/material/styles';
import { Box, Drawer, Button } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';

import NavSection from '../../../components/nav-section';
import navConfig from './config';
import { logout } from "../../../store/actions/auth"
import Page from '../../../data/Page';

const NAV_WIDTH = 280;

const DrawerPaper = styled('div')(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '0.5rem',
  background: 'linear-gradient(180deg, #111827 0%, #0f172a 50%, #0b1120 100%)',
  borderRight: '1px solid rgba(255,255,255,0.06)',
}));

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const navigate = useNavigate()

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <DrawerPaper>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%', textAlign: 'center', paddingTop: 24 }}
      >
        <Box sx={{
          px: 5, py: 2,
          fontSize: '22px',
          fontWeight: '800',
          letterSpacing: '1px',
          background: 'linear-gradient(135deg, #00B74F 0%, #4F46E5 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          TroopersGaming
        </Box>
        <Box sx={{ mx: 3, mt: 1, mb: 0.5, height: 1, bgcolor: 'rgba(255,255,255,0.06)' }} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        style={{ width: '100%', flex: 1, overflow: 'auto', px: 1 }}
      >
        <NavSection data={navConfig} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        style={{ width: '100%', padding: '0 20px 20px' }}
      >
        <Button
          fullWidth
          sx={{
            color: 'rgba(255,255,255,0.6)',
            borderColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2,
            py: 1.2,
            '&:hover': {
              borderColor: 'rgba(255, 75, 75, 0.5)',
              color: '#FF4B4B',
              bgcolor: 'rgba(255, 75, 75, 0.08)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease',
          }}
          variant="outlined"
          onClick={() => {
            navigate(Page.LOGIN)
            logout()
          }}
        >
          Logout
        </Button>
      </motion.div>
    </DrawerPaper>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'transparent',
              border: 'none',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: '#111827',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
